import { LoginWall } from "components/App/components/LoginWall";
import { CLITokenGenerationPage } from "components/pages/CLITokenGenerationPage";
import { compiledClientConfig } from "config";
import { PageProvider as NewPageProvider } from "context/new/pageContext";
import { PageProvider } from "context/pageContext";
import { useGlobalStyles } from "globalStyles";
import { useGainsightPX } from "hooks/useGainsightsPX";
import { StrictMode, useCallback, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { BrowserRouter, Route, Routes } from "react-router";
import { devLog } from "utils/devtools/devLogging";
import { logrocketLogError } from "utils/logrocket";
import { CHECK_EVENT, checkForNewVersionEventEmitter } from "utils/version";

import { GlobalErrorModal } from "./components/GlobalErrorModal";
import { Layout } from "./components/Layout";
import { MicrosoftTeamsRouter } from "./components/MicrosoftTeamsRouter";
import { Providers } from "./Providers";
import { useStyles } from "./styles";

const getLastCommitHash = async () => {
  const data = await (await fetch("/deploymentInfo")).json();
  return data["COMMIT_HASH"] as string;
};

const useKeepAppUpToDate = (production: boolean) => {
  const lastCommitHashRef = useRef<string>(undefined);
  const intervalRef = useRef<number>(undefined);

  const checkUpdatedCommitHash = useCallback(async () => {
    try {
      if (lastCommitHashRef.current && lastCommitHashRef.current !== (await getLastCommitHash())) {
        window.location.reload();
      }
    } catch (error) {
      devLog({ level: "error", message: "failed to fetch last commit hash, will try again soon" });
      logrocketLogError(
        error instanceof Error ? error : new Error("failed to fetch last commit hash, will try again soon"),
      );
    }
  }, []);
  const update = useCallback(async () => {
    try {
      lastCommitHashRef.current = await getLastCommitHash();

      if (intervalRef.current) window.clearInterval(intervalRef.current);

      intervalRef.current = window.setInterval(
        () => void checkUpdatedCommitHash(),
        compiledClientConfig.upToDateCheckInterval,
      );
    } catch (error) {
      devLog({ level: "error", message: "failed to fetch last commit hash, client won't refresh automatically" });
      logrocketLogError(
        error instanceof Error
          ? error
          : new Error("failed to fetch last commit hash, client won't refresh automatically"),
      );
    }
  }, [checkUpdatedCommitHash]);

  useEffect(() => {
    if (production) void update();
    return () => {
      if (intervalRef.current) window.clearInterval(intervalRef.current);
    };
  }, [production, update]);

  useEffect(() => {
    const visibilityChange = () => {
      if (document.visibilityState === "visible") {
        void checkUpdatedCommitHash();
      }
    };
    document.addEventListener("visibilitychange", visibilityChange);
    return () => document.removeEventListener("visibilitychange", visibilityChange);
  }, [checkUpdatedCommitHash]);

  useEffect(() => {
    const callback = () => {
      void checkUpdatedCommitHash();
    };
    checkForNewVersionEventEmitter.on(CHECK_EVENT, callback);

    return () => {
      checkForNewVersionEventEmitter.off(CHECK_EVENT, callback);
    };
  }, [checkUpdatedCommitHash]);
};

const Routers: FC = () => {
  useGainsightPX();

  return (
    <BrowserRouter>
      <PageProvider>
        <NewPageProvider>
          <MicrosoftTeamsRouter>
            <LoginWall>
              <Routes>
                <Route element={<CLITokenGenerationPage />} key="CLILoginGeneration" path="/cliLogin" />
                <Route element={<Layout />} path="*" />
              </Routes>
            </LoginWall>
          </MicrosoftTeamsRouter>
        </NewPageProvider>
      </PageProvider>
    </BrowserRouter>
  );
};

export default function App() {
  const { t } = useTranslation();
  const classes = useStyles();
  useGlobalStyles();
  useKeepAppUpToDate(compiledClientConfig.isProd);

  useEffect(() => {
    const direction = t("direction");

    document.body.classList.toggle("ltr", direction === "ltr");
    document.body.classList.toggle("rtl", direction === "rtl");
  }, [t]);

  return (
    <div className={classes.app}>
      <StrictMode>
        <Providers>
          <GlobalErrorModal />
          <Routers />
        </Providers>
      </StrictMode>
    </div>
  );
}
